import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { StateService } from '@uirouter/core';
import { CrConfigService, ToastService } from '../../../../ng1-factories';

/**
 * Entitlement Module V3 wrapper component.
 *
 * Loads the Entitlement Module React app from /entitlement-module/
 * (same-origin) inside an iframe. Source code lives in
 * src/app/client/entitlement-angular/entitlement-v3/ and is built by
 * `npm run build` (Vite, --mode embedded) into src/assets/entitlement-module/.
 *
 * Authentication:
 *   - Same-origin cookies provide baseline session access
 *   - CR session context (customerId, sourceSystem, env, region) is also
 *     passed via postMessage after the iframe loads
 *   - The React app validates the session by hitting /auth/me before
 *     rendering its main UI
 *
 * Initial context (customerId, sourceSystem) is also passed via URL query
 * params so the React app can boot without waiting for the postMessage.
 *
 * NOTE: sourceSystem currently reads from `state.params.sourceSystem` with
 * an empty fallback. Once cr-config exposes `source_system` in customer
 * config, replace `resolveSourceSystem()` to read from there.
 */

/**
 * Match a bare hostname segment (delimited by dots or string boundaries)
 * rather than a raw substring, so `stage` matches `foo.stage.bar.com`
 * but NOT `foostage.evil.com` or `bar.com.foo-stage`.
 */
function isSegment(hostname: string, segment: string): boolean {
    return new RegExp(`(^|\\.)${segment}(\\.|$)`, 'i').test(hostname);
}

@Component({
    selector: 'cr-entitlement-v3',
    templateUrl: './entitlement-v3.component.html',
    styleUrls: ['./entitlement-v3.component.scss'],
})
export class EntitlementV3Component implements OnInit, OnDestroy {
    @ViewChild('entitlementFrame', { static: false })
    entitlementFrame: ElementRef<HTMLIFrameElement>;

    iframeSrc: SafeResourceUrl;
    isLoading = true;

    private messageHandler: (event: MessageEvent) => void;

    constructor(
        private sanitizer: DomSanitizer,
        private state: StateService,
        private crToastService: ToastService,
        private crConfig: CrConfigService,
    ) {}

    ngOnInit(): void {
        const params = new URLSearchParams({
            customerId: this.state.params.customerId || '',
            sourceSystem: this.resolveSourceSystem(),
            venueId: this.resolveVenueId(),
            isAdmin: this.resolveIsAdmin() ? 'true' : 'false',
            env: this.detectEnvironment(),
            region: this.detectRegion(),
            imageServiceUrl: this.resolveImageServiceUrl(),
            embedded: 'true',
        });

        const iframeUrl = `/entitlement-module/index.html?${params.toString()}`;
        this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(iframeUrl);

        this.messageHandler = (event: MessageEvent) => this.handleMessage(event);
        window.addEventListener('message', this.messageHandler);
    }

    ngOnDestroy(): void {
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
        }
    }

    onIframeLoad(): void {
        this.isLoading = false;
        this.sendSessionToIframe();
    }

    /**
     * Send session context to the Entitlement Module iframe via postMessage.
     * Provides explicit authentication proof beyond same-origin cookies so
     * the React app can validate before making API calls.
     */
    private sendSessionToIframe(): void {
        const iframe = this.entitlementFrame?.nativeElement;
        if (!iframe?.contentWindow) {
            return;
        }
        iframe.contentWindow.postMessage(
            {
                type: 'entitlement-session',
                customerId: this.state.params.customerId || '',
                sourceSystem: this.resolveSourceSystem(),
                venueId: this.resolveVenueId(),
                isAdmin: this.resolveIsAdmin(),
                env: this.detectEnvironment(),
                region: this.detectRegion(),
                imageServiceUrl: this.resolveImageServiceUrl(),
            },
            window.location.origin,
        );
    }

    /**
     * Resolve the customer-specific image service base URL from cr-config
     * (`customer.serviceUrls.image`). The React side stores uploaded image
     * `src` as a relative path (e.g. `/<customer>/entitlements/<uuid>/x.png`);
     * combining the two at render time produces a fully-qualified URL.
     *
     * Returns empty string when cr-config doesn't expose the field — the
     * React side then falls back to using the raw `src`, which is fine if
     * it happens to already be absolute (legacy data).
     */
    private resolveImageServiceUrl(): string {
        const customer = (this.crConfig as { customer?: { serviceUrls?: { image?: string } } })?.customer || {};
        return customer.serviceUrls?.image || '';
    }

    /**
     * Resolve venueId from CR state. Drives utm_source for Quick/Cross Sell
     * links inside the React app (`${venueId}_mobileApp`). Empty when the
     * page wasn't opened with a venue context.
     */
    private resolveVenueId(): string {
        return this.state.params.venueId || '';
    }

    /**
     * Resolve whether the current CR user has admin role. Gates the
     * Settings (gear) page inside the React app — content-admins keep
     * access to the module itself but can't edit per-customer label /
     * criteria-field catalogs.
     *
     * Phase 1 default: `true` so the gear is visible while we iterate. In
     * Phase 2 this should consult the same Casbin / cr-config role lookup
     * that other admin-only modules use.
     */
    private resolveIsAdmin(): boolean {
        // TODO Phase 2: hook into Casbin / cr-config role membership.
        return true;
    }

    /**
     * Resolve the source_system value for this session.
     *
     * Order of precedence:
     *   1. URL state param `sourceSystem` (so dev/QA can override per-route)
     *   2. cr-config `customer.source_system` (snake_case — current convention)
     *   3. cr-config `customer.sourceSystem` (camelCase fallback in case the
     *      backend names it that way later)
     *   4. Empty string — the React app surfaces a banner saying it's missing.
     */
    private resolveSourceSystem(): string {
        const fromState = this.state.params.sourceSystem;
        if (fromState) {
            return fromState;
        }
        const customer = this.crConfig?.customer || {};
        return customer.source_system || customer.sourceSystem || '';
    }

    private detectEnvironment(): string {
        const hostname = window.location.hostname;
        // Use segment-based matching so an attacker-controlled host like
        // `evil-control-room.te2.io.attacker.com` can't be classified as
        // prod (CodeQL: incomplete URL substring sanitization).
        if (isSegment(hostname, 'stage')) {
            return 'stage';
        }
        if (hostname === 'control-room.te2.io' || hostname.endsWith('.control-room.te2.io')) {
            return 'prod';
        }
        // Local dev — default to stage since webpack devServer proxies to a real env
        return 'stage';
    }

    private detectRegion(): string {
        const hostname = window.location.hostname;
        if (isSegment(hostname, 'ap')) {
            return 'ap';
        }
        return 'us';
    }

    private handleMessage(event: MessageEvent): void {
        if (event.origin !== window.location.origin) {
            return;
        }

        const data = event.data;
        if (!data || !data.type) {
            return;
        }

        switch (data.type) {
            case 'entitlement-resize':
                // Iframe fills viewport via CSS — no dynamic height needed
                break;

            case 'entitlement-toast':
                if (data.msg) {
                    this.crToastService.toast(data.msg, data.timeToLive || 3000);
                }
                break;

            case 'entitlement-ready':
                this.isLoading = false;
                // Re-send session in case the iframe loaded before React mounted
                this.sendSessionToIframe();
                break;

            case 'entitlement-navigate':
                if (data.route) {
                    this.state.go(data.route, data.params || {});
                }
                break;
        }
    }
}
